(function () {
  // Pack cards come only from the theme's own `_pack` line property, set on
  // every bag the buy box and combo packs add. Kaching's discount title
  // ("3-Pack") can't be used: it also lands on 3+ single bags of one color,
  // which stay a normal line with a quantity.
  const PACK_TITLES = {
    pack3: { id: 'pack3', title: '3-Pack' },
    pack2: { id: 'pack2', title: '2-Pack' },
  };
  const GIFT_NAME = 'Free with 3-Pack';
  const READY_COPY = 'Ready to ship';

  const money = (cents) => {
    const n = Number(cents) / 100;
    return n % 1 === 0 ? `$${n.toFixed(0)}` : `$${n.toFixed(2)}`;
  };

  const rootUrl = () => window.Shopify?.routes?.root || '/';

  // /cart.js puts line discounts in `line_level_discount_allocations`, each
  // with its `discount_application` inline. The older `discount_allocations`
  // shape (index into cart.discount_applications) is kept as a fallback.
  const discountTitles = (item, cart) => {
    const inline = (item.line_level_discount_allocations || []).map((d) => d.discount_application?.title);
    const indexed = (item.discount_allocations || []).map((d) => {
      const app = (cart.discount_applications || [])[d.discount_application_index];
      return app?.title || d.title;
    });
    return inline.concat(indexed).filter(Boolean);
  };

  // One card per pack added: the type (`_pack`) plus that add's `_pack_ref`.
  // Lines from before refs existed share one card per type, as they did.
  const packKeyFor = (item) => {
    const prop = item.properties?._pack;
    if (!prop || !PACK_TITLES[prop]) return null;
    return `${prop}:${item.properties?._pack_ref || ''}`;
  };

  const isGift = (item, cart) =>
    item.final_line_price === 0 ||
    discountTitles(item, cart).includes(GIFT_NAME) ||
    item.properties?._pack === 'gift';

  // Kaching titles every 3+ bag price break "3-Pack" whatever the quantity,
  // so "Qty 6" next to "3-PACK" read as six 3-packs. Bag lines on that price
  // show what each bag costs instead; other discounts keep their own title.
  const BUNDLE_TITLE = /\bpack\b|bundle/i;
  const lineTag = (item, cart) => {
    const title = discountTitles(item, cart)[0] || '';
    if (!title || isGift(item, cart) || !BUNDLE_TITLE.test(title) || item.quantity < 1) return title;
    return `Bundle price: ${money(Math.round(item.final_line_price / item.quantity))} a bag`;
  };

  const arrivalOf = (item) => item.properties?._arrival || '';
  const isPreorder = (item) => item.properties?._preorder === 'true' || Boolean(arrivalOf(item));

  const shortDate = (arrival) => {
    if (!arrival) return '';
    const d = new Date(`${arrival}T12:00:00`);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Per-line ship copy. The merchant's override wins, then the date, then TBD.
  const shipLabel = (item) => {
    if (!isPreorder(item)) return '';
    const override = item.properties?._override;
    const date = shortDate(arrivalOf(item));
    if (override) return date && !/\d/.test(override) ? `${override}: ${date}` : override;
    return date ? `Pre-order: ships ${date}` : 'Pre-order: ship date coming soon';
  };

  // A pack ships as one parcel, so one status covers it: the pre-order bag
  // with the latest arrival date decides (ISO dates sort as strings). Bags
  // with no date yet only win when nothing in the pack has a date.
  const packShipItem = (items) => {
    const pre = items.filter(isPreorder);
    if (!pre.length) return null;
    return pre.reduce((latest, item) => (arrivalOf(item) > arrivalOf(latest) ? item : latest));
  };

  // Same dot + copy pattern as the buybox (.stock-status in slb-pdp.css).
  const statusHtml = (waitCopy, extraClass) => {
    const state = waitCopy ? 'stock-status--wait' : 'stock-status--ready';
    return `
      <p class="stock-status ${state} ${extraClass}">
        <span class="stock-status-dot" aria-hidden="true"></span>
        <span>${waitCopy || READY_COPY}</span>
      </p>`;
  };

  // Whole-order shipping note. SLB never splits an order: if anything in the
  // cart is a pre-order, everything waits for it. Returned as '' when the cart
  // has no pre-orders so the cart attribute can be cleared.
  const ORDER_NOTE_KEY = 'Shipping note';
  const orderShipNote = (items) => {
    const pre = items.filter(isPreorder);
    if (!pre.length) return '';
    const latest = pre.map(arrivalOf).filter(Boolean).sort().pop();
    const when = latest ? `est. ${shortDate(latest)}` : 'date to be confirmed';
    return `Includes a pre-order. We don\u2019t split shipments, so your whole order ships together, ${when}.`;
  };

  const groupCart = (cart) => {
    const packs = {};
    const lines = [];
    const gifts = [];

    (cart.items || []).forEach((item) => {
      if (isGift(item, cart)) {
        gifts.push(item);
        return;
      }
      const key = packKeyFor(item);
      if (key) {
        if (!packs[key]) packs[key] = { key, type: item.properties._pack, items: [], gift: null };
        packs[key].items.push(item);
        return;
      }
      lines.push(item);
    });

    // The gift rides in the 3-Pack card. Earned without one (3+ singles),
    // it is a line of its own rather than disappearing.
    gifts.forEach((gift) => {
      const host = Object.values(packs).find((p) => p.type === 'pack3' && !p.gift);
      if (host) host.gift = gift;
      else lines.push(gift);
    });

    return { packs: Object.values(packs), lines };
  };

  const initCart = () => {
    const root = document.getElementById('cart-root');
    if (!root) return;

    // Subtotal threshold in cents, from the theme's free shipping limit; it
    // must match checkout's "Ships Free over $70" discount.
    const freeShippingCents = Math.max(0, Math.round(Number(root.dataset.freeShippingCents) || 0));
    // Team orders: at teamBags bags (of the listed products) Checkout gives
    // way to the team order note. 0 = off.
    const teamBags = Math.max(0, Math.round(Number(root.dataset.teamBags) || 0));
    const teamProducts = (root.dataset.teamProducts || '').split(',').filter(Boolean);
    const bodyEl = document.getElementById('cart-body');
    const countEl = document.getElementById('cart-count');
    const shipEl = document.getElementById('cart-ship');
    const shipCopyEl = document.getElementById('cart-ship-copy');
    const shipFillEl = document.getElementById('cart-ship-fill');
    const totalEl = document.getElementById('cart-total');
    const savedEl = document.getElementById('cart-saved');
    const notesEl = document.getElementById('cart-notes');
    const closeBtn = document.getElementById('cart-close');
    const panel = root.querySelector('.cart-panel');
    const headerBtns = document.querySelectorAll('[data-cart-open]');

    const lockScroll = (on) => {
      if (window.SLB?.lockScroll) window.SLB.lockScroll('cart', on);
      else document.body.style.overflow = on ? 'hidden' : '';
    };

    const focusables = () =>
      Array.from(
        panel?.querySelectorAll('a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])') || []
      ).filter((el) => el.getClientRects().length > 0);

    // An add button disables itself while it adds, which drops its focus
    // before the drawer opens. The control that lost focus just before
    // opening counts as the opener, and focus goes back once it is enabled.
    let lastOutside = null;
    let lastOutsideAt = 0;
    document.addEventListener('focusout', (e) => {
      if (root.contains(e.target)) return;
      lastOutside = e.target;
      lastOutsideAt = Date.now();
    });
    const returnFocus = (el) => {
      if (!el || !el.isConnected || root.contains(el)) return;
      const started = Date.now();
      const attempt = () => {
        const active = document.activeElement;
        if (active && active !== document.body && !root.contains(active)) return;
        if (!el.disabled) el.focus({ preventScroll: true });
        else if (Date.now() - started < 2500) window.setTimeout(attempt, 100);
      };
      attempt();
    };

    // Closed, the drawer is inert (out of the tab order and the a11y tree)
    // and only claims to be a modal while it is one.
    let opener = null;
    const isOpen = () => root.getAttribute('data-open') === 'true';
    const setOpen = (open) => {
      if (open === isOpen()) return;
      root.setAttribute('data-open', String(open));
      root.inert = !open;
      if (open) panel?.setAttribute('aria-modal', 'true');
      else panel?.removeAttribute('aria-modal');
      headerBtns.forEach((btn) => btn.setAttribute('aria-expanded', String(open)));
      lockScroll(open);
      if (open) {
        const active = document.activeElement;
        opener = active && active !== document.body ? active : Date.now() - lastOutsideAt < 3000 ? lastOutside : null;
        (closeBtn || focusables()[0])?.focus();
      } else {
        returnFocus(opener);
        opener = null;
      }
    };
    root.inert = !isOpen();

    headerBtns.forEach((el) => el.addEventListener('click', () => setOpen(true)));
    document.getElementById('cart-scrim')?.addEventListener('click', () => setOpen(false));
    closeBtn?.addEventListener('click', () => setOpen(false));
    document.addEventListener('keydown', (e) => {
      if (!isOpen()) return;
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const list = focusables();
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      const inside = panel.contains(document.activeElement);
      if (e.shiftKey && (!inside || document.activeElement === first)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || document.activeElement === last)) {
        e.preventDefault();
        first.focus();
      }
    });
    // Safari's default Tab skips links, so it can step past the last stop
    // without the Tab handler above seeing it; pull focus back in.
    document.addEventListener('focusin', (e) => {
      if (!isOpen() || panel.contains(e.target)) return;
      (focusables()[0] || panel).focus();
    });
    document.addEventListener('slb:cart:open', () => setOpen(true));

    const shipCopy = (count, left, bagPrice) => {
      if (left === 0) return 'Free standard shipping unlocked \u2713';
      if (count === 0) return 'Free standard shipping on 2+ bags';
      if (bagPrice > 0 && left <= bagPrice) return 'Add 1 more bag and shipping\u2019s free';
      return `${money(left)} away from free shipping`;
    };

    const renderShipping = (cart) => {
      if (!shipEl) return;
      const count = cart.item_count || 0;
      const total = cart.total_price || 0;
      const bag = (cart.items || []).find((i) => teamProducts.includes(i.handle));
      const left = freeShippingCents > 0 ? Math.max(0, freeShippingCents - total) : 0;
      const percent = freeShippingCents > 0 ? Math.min(100, Math.floor((total / freeShippingCents) * 100)) : 100;
      shipEl.dataset.unlocked = String(left === 0);
      if (shipCopyEl) shipCopyEl.textContent = shipCopy(count, left, bag ? bag.final_price : 0);
      if (shipFillEl) shipFillEl.style.width = `${percent}%`;
    };

    // A re-render replaces the rows, so a focused qty or remove button goes
    // with it. Note which control had focus and land on its replacement, or
    // on the close button when its line is gone.
    const focusedControl = () => {
      const el = document.activeElement;
      if (!el || !bodyEl?.contains(el)) return null;
      return { key: el.closest('[data-line-key]')?.dataset.lineKey || '', qty: el.dataset.qty || '' };
    };
    const restoreFocus = (was) => {
      if (!was) return;
      const row = was.key ? bodyEl.querySelector(`[data-line-key="${was.key}"]`) : null;
      const target = (row && was.qty && row.querySelector(`[data-qty="${was.qty}"]`)) || closeBtn;
      target?.focus();
    };

    const renderTeam = (cart) => {
      const teamEl = document.getElementById('cart-team');
      const checkoutEl = document.getElementById('cart-checkout');
      if (!teamBags || !teamEl || !checkoutEl) return;
      const bags = (cart.items || []).reduce((n, item) => n + (teamProducts.includes(item.handle) ? item.quantity : 0), 0);
      const locked = bags >= teamBags;
      teamEl.hidden = !locked;
      checkoutEl.hidden = locked;
      root.querySelector('.cart-foot')?.classList.toggle('cart-foot--team', locked);
    };

    const render = (cart) => {
      const grouped = groupCart(cart);
      const itemCount = cart.item_count || 0;
      const emptyEl = document.getElementById('cart-empty');
      const hadFocus = focusedControl();
      if (countEl) countEl.textContent = String(itemCount);
      headerBtns.forEach((btn) => btn.setAttribute('data-cart-count', String(itemCount)));

      renderShipping(cart);
      renderTeam(cart);

      if (totalEl) totalEl.textContent = money(cart.total_price);
      const saved = (cart.original_total_price || 0) - (cart.total_price || 0);
      if (savedEl) {
        savedEl.textContent = saved > 0 ? `You saved ${money(saved)}` : '';
        savedEl.hidden = saved <= 0;
      }
      if (notesEl) notesEl.hidden = itemCount === 0;
      const footEl = root.querySelector('.cart-foot');
      if (footEl) footEl.style.display = itemCount === 0 ? 'none' : '';

      if (!bodyEl) return;
      if (emptyEl) emptyEl.hidden = itemCount > 0;
      if (itemCount === 0) {
        bodyEl.querySelectorAll('.cart-row, .cart-preorder-note').forEach((n) => n.remove());
        restoreFocus(hadFocus);
        syncOrderNote(cart, '');
        return;
      }

      const parts = [];
      const shipNote = orderShipNote(cart.items || []);
      if (shipNote) parts.push(`<p class="cart-preorder-note">${shipNote}</p>`);

      grouped.packs.forEach((pack) => {
        const meta = PACK_TITLES[pack.type] || { title: pack.type };
        const full = pack.items.reduce((sum, i) => sum + i.original_line_price, 0);
        const paid = pack.items.reduce((sum, i) => sum + i.final_line_price, 0);
        const keys = [...pack.items, pack.gift].filter(Boolean).map((i) => i.key);
        const waitsOn = packShipItem(pack.items);
        parts.push(`
          <div class="cart-row cart-row--pack">
            <section class="pack" data-pack-keys="${keys.join(',')}">
              <div class="pack-head">
                <h3 class="display pack-title">${meta.title}</h3>
                <p class="pack-prices">
                  ${full > paid ? `<span class="pack-was">${money(full)}</span>` : ''}
                  <span class="pack-now">${money(paid)}</span>
                </p>
              </div>
              <ul class="pack-lines">
                ${pack.items
                  .map((item) => {
                    const eta = shipLabel(item);
                    return `
                      <li class="pack-line">
                        <span class="line-thumb"><img src="${item.image}" alt="" loading="lazy" /></span>
                        <span class="line-name">${item.variant_title || item.product_title}${
                          item.quantity > 1
                            ? ` <span aria-hidden="true">\u00d7${item.quantity}</span><span class="sr-only">, ${item.quantity} bags</span>`
                            : ''
                        }</span>
                        ${eta ? `<span class="line-eta">${eta}</span>` : ''}
                      </li>`;
                  })
                  .join('')}
              </ul>
              ${
                pack.gift
                  ? `<div class="pack-gift">
                      <span class="line-thumb line-thumb--gift"><img src="${pack.gift.image}" alt="" loading="lazy" /></span>
                      <span class="gift-name">${pack.gift.product_title}</span>
                      <span class="gift-free">Free</span>
                    </div>`
                  : ''
              }
              <div class="pack-foot">
                ${statusHtml(waitsOn ? shipLabel(waitsOn) : '', 'pack-status')}
                <button class="pack-remove" type="button" data-remove-pack>Remove pack</button>
              </div>
            </section>
          </div>`);
      });

      grouped.lines.forEach((item) => {
        const tag = lineTag(item, cart);
        parts.push(`
          <section class="cart-row line" data-line data-line-key="${item.key}">
            <span class="line-thumb line-thumb--lg"><img src="${item.image}" alt="" loading="lazy" /></span>
            <div class="line-main">
              <div class="line-top">
                <div class="line-heading">
                  <p class="line-title">${item.product_title}</p>
                  ${item.variant_title ? `<p class="line-variant">${item.variant_title}</p>` : ''}
                </div>
                <p class="line-price">
                  ${
                    item.original_line_price > item.final_line_price
                      ? `<span class="line-was">${money(item.original_line_price)}</span>`
                      : ''
                  }
                  <span>${money(item.final_line_price)}</span>
                </p>
              </div>
              ${statusHtml(shipLabel(item), 'line-status')}
              ${tag ? `<p class="line-tag">${tag}</p>` : ''}
              <div class="line-actions">
                <div class="line-qty">
                  <button type="button" aria-label="Decrease quantity" data-qty="-1">&minus;</button>
                  <span data-qty-value>${item.quantity}</span>
                  <button type="button" aria-label="Increase quantity" data-qty="1">+</button>
                </div>
                <button class="line-remove" type="button" data-remove-line>Remove</button>
              </div>
            </div>
          </section>`);
      });

      bodyEl.innerHTML = parts.join('') + (emptyEl ? emptyEl.outerHTML : '');
      const nextEmpty = bodyEl.querySelector('#cart-empty');
      if (nextEmpty) nextEmpty.hidden = itemCount > 0;
      bindBody();
      restoreFocus(hadFocus);
      syncOrderNote(cart, shipNote);
    };

    // Keep a cart attribute in step with the drawer's shipping note so it lands
    // on the order (admin "Additional details", confirmation email). Only writes
    // when the value actually changed, and the returned cart already matches,
    // so this can't loop.
    let noteSyncing = false;
    const syncOrderNote = (cart, note) => {
      const current = (cart.attributes || {})[ORDER_NOTE_KEY] || '';
      if (noteSyncing || current === note) return;
      noteSyncing = true;
      updateCart({ attributes: { [ORDER_NOTE_KEY]: note } })
        .then((next) => {
          noteSyncing = false;
          if (next && next.items) render(next);
        })
        .catch(() => {
          noteSyncing = false;
        });
    };

    const fetchCart = () =>
      fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

    // Cart writes go out one at a time: overlapping /cart/update.js calls
    // (order note, gift note, shipping note) overwrite each other.
    let writeQueue = Promise.resolve();
    let pendingWrites = 0;
    const queueWrite = (send) => {
      pendingWrites += 1;
      const run = writeQueue.then(send, send);
      writeQueue = run
        .catch(() => {})
        .then(() => {
          pendingWrites -= 1;
        });
      return run;
    };

    const updateCart = (payload, options = {}) =>
      queueWrite(() =>
        fetch(`${rootUrl()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
          ...options,
        }).then((r) => r.json())
      );

    const changeLine = (key, quantity) =>
      queueWrite(() =>
        fetch(`${rootUrl()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity }),
        }).then((r) => r.json())
      );

    // Order note (cart.note) and gift note (cart attribute). The fields live
    // outside #cart-body so re-renders never wipe what the shopper typed.
    // Saved on change/blur and shortly after typing stops; keepalive lets the
    // last save finish if they head straight to checkout. A save still
    // waiting its turn in the queue would not survive the navigation, so
    // Checkout waits for the queue (up to a few seconds) first.
    const noteSavers = [];
    const bindCheckout = () => {
      const link = root.querySelector('.cart-checkout');
      link?.addEventListener('click', (e) => {
        noteSavers.forEach((save) => save());
        if (!pendingWrites || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault();
        let gone = false;
        const go = () => {
          if (gone) return;
          gone = true;
          window.location.href = link.href;
        };
        writeQueue.then(go);
        window.setTimeout(go, 3000);
      });
    };

    const bindNotes = () => {
      document.querySelectorAll('[data-note-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const field = document.getElementById(btn.getAttribute('aria-controls'));
          const open = btn.getAttribute('aria-expanded') !== 'true';
          btn.setAttribute('aria-expanded', String(open));
          if (!field) return;
          field.hidden = !open;
          if (open) field.querySelector('textarea')?.focus();
        });
      });

      document.querySelectorAll('[data-cart-note]').forEach((field) => {
        let saved = field.value.trim();
        let timer = null;
        const save = () => {
          window.clearTimeout(timer);
          const value = field.value.trim();
          if (value === saved) return;
          saved = value;
          const payload =
            field.dataset.cartNote === 'note'
              ? { note: value }
              : { attributes: { [field.dataset.attribute]: value } };
          updateCart(payload, { keepalive: true }).catch(() => {
            saved = null;
          });
        };
        noteSavers.push(save);
        field.addEventListener('change', save);
        field.addEventListener('blur', save);
        field.addEventListener('input', () => {
          window.clearTimeout(timer);
          timer = window.setTimeout(save, 700);
        });
      });
    };

    const bindBody = () => {
      bodyEl.querySelectorAll('[data-remove-pack]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const pack = btn.closest('.pack');
          const keys = (pack?.dataset.packKeys || '').split(',').filter(Boolean);
          const updates = {};
          keys.forEach((k) => {
            updates[k] = 0;
          });
          const cart = await updateCart({ updates });
          render(cart);
        });
      });

      bodyEl.querySelectorAll('[data-remove-line]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const key = btn.closest('[data-line]')?.dataset.lineKey;
          if (!key) return;
          render(await changeLine(key, 0));
        });
      });

      bodyEl.querySelectorAll('[data-qty]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const line = btn.closest('[data-line]');
          const key = line?.dataset.lineKey;
          const current = Number(line?.querySelector('[data-qty-value]')?.textContent || 1);
          const next = Math.max(0, current + Number(btn.dataset.qty));
          if (!key) return;
          render(await changeLine(key, next));
        });
      });
    };

    const refresh = () => fetchCart().then(render).catch(() => {});

    bindNotes();
    bindCheckout();
    document.addEventListener('theme:cart:refresh', refresh);
    refresh();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCart);
  } else {
    initCart();
  }
})();
